import type * as Immutable from 'immutable';
import { createMigration } from './utils';

const CIRCLE_ASSET_CLASS_TYPE = 'CircleAsset';

/**
 * Migration to update CircleAsset anchors after the anchor fix.
 *
 * Previously, CircleAsset drew its circle at (0, 0), creating bounds centered at origin.
 * Now it draws at (centerOffset, centerOffset), creating bounds starting at (0, 0).
 *
 * To preserve visual behavior, add 0.5 to both anchor x and y values.
 * This shifts the anchor reference point to account for the new bounds origin.
 */
export const migrationTo9_233 = createMigration({
    migrateVideo(video) {
        return video.withMutations((map) => {
            map.get('slides').forEach((scene: Immutable.Map<string, unknown>, i: number) => {
                map.setIn(['slides', i], this.migrateScene(scene));
            });
        });
    },
    migrateScene(scene) {
        return scene.withMutations((map) => {
            const children = map.get('children') as
                | Immutable.List<Immutable.Map<string, unknown>>
                | undefined;
            if (!children) {
                return;
            }

            children.forEach((child: Immutable.Map<string, unknown>, cIndex: number) => {
                const migratedChild = migrateAsset(child);
                map.setIn(['children', cIndex], migratedChild);
            });
        });
    },
});

function migrateAsset(asset: Immutable.Map<string, unknown>): Immutable.Map<string, unknown> {
    const classType = asset.get('classType') as string;

    if (classType === CIRCLE_ASSET_CLASS_TYPE) {
        return migrateCircleAsset(asset);
    }

    // Check for nested assets in groups
    const nestedChildren = asset.get('children') as
        | Immutable.List<Immutable.Map<string, unknown>>
        | undefined;
    if (nestedChildren) {
        return asset.withMutations((map) => {
            nestedChildren.forEach((child: Immutable.Map<string, unknown>, index: number) => {
                map.setIn(['children', index], migrateAsset(child));
            });
        });
    }

    return asset;
}

function migrateCircleAsset(asset: Immutable.Map<string, unknown>): Immutable.Map<string, unknown> {
    const transformation = asset.get('transformation') as
        | Immutable.Map<string, unknown>
        | undefined;
    if (!transformation) {
        return asset;
    }

    const anchor = transformation.get('anchor') as Immutable.Map<string, unknown> | undefined;
    const anchorX = (anchor?.get('x') as number) ?? 0;
    const anchorY = (anchor?.get('y') as number) ?? 0;

    return asset.withMutations((map) => {
        map.setIn(['transformation', 'anchor', 'x'], Math.min(anchorX + 0.5, 1));
        map.setIn(['transformation', 'anchor', 'y'], Math.min(anchorY + 0.5, 1));
    });
}
